import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, ExternalLink, Lock, Sparkles, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import LeanGauge from "@/components/LeanGauge";
import LoadingStepper from "@/components/LoadingStepper";
import OutletFavicon from "@/components/OutletFavicon";
import ClickSpark from "@/components/reactbits/ClickSpark";
import { analyzeFeedArticle, errDetail, ERROR_CODES, getFeedArticle, leanStyle } from "@/lib/api";
import { useThemeToken } from "@/lib/theme";
import { when } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

const POLL_MS = 2000;
const POLL_LIMIT = 15;   // 30s, then stop and let them try again

/**
 * The click target for a ticker card: a glimpse of the article, and the button
 * that turns it into a scored report.
 *
 * Reading an already-scored article is free and unmetered — the analysis was
 * paid for by whoever clicked it first. Only an unscored article costs a scan.
 */
export default function ArticlePeekDialog({ articleId, open, onOpenChange }) {
  const [article, setArticle] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState("");
  const [wall, setWall] = useState(null);       // 'signup' | 'limit'
  const [pollTick, setPollTick] = useState(0);  // drives the poll's re-arm
  const navigate = useNavigate();
  const { refreshUsage } = useAuth();
  // Sparks are drawn on a canvas, so this one can't be a CSS variable.
  const ink = useThemeToken("--c-ink");
  const polls = useRef(0);

  // The dialog closing does not unmount this component — both NewsTicker and
  // Feed render it unconditionally — so in-flight work has to check for itself
  // whether anyone is still looking.
  const openRef = useRef(open);
  useEffect(() => { openRef.current = open; }, [open]);

  useEffect(() => {
    if (!open || !articleId) return;
    let live = true;
    setArticle(null); setError(""); setAnalyzing(false); polls.current = 0;
    getFeedArticle(articleId)
      .then((a) => live && setArticle(a))
      .catch(() => live && setError("Couldn't load that article."));
    return () => { live = false; };
  }, [articleId, open]);

  // Somebody else claimed this article a moment before we did; their analysis
  // is the one we'll show, so wait for it rather than paying for a second.
  useEffect(() => {
    if (!open || !analyzing || article?.status !== "scoring") return;
    const timer = setTimeout(async () => {
      if (polls.current >= POLL_LIMIT) {
        setAnalyzing(false);
        setError("This is taking longer than expected. Try again in a moment.");
        return;
      }
      polls.current += 1;
      try {
        const fresh = await getFeedArticle(articleId);
        if (!openRef.current) return;                    // dismissed mid-poll
        if (fresh.status === "scored" && fresh.report_id) {
          navigate(`/report/${fresh.report_id}`);
          return;
        }
        if (fresh.status === "failed") {
          setAnalyzing(false);
          setError(fresh.error || "This article couldn't be read.");
          return;
        }
        setArticle(fresh);
      } catch { /* transient — fall through and try again */ }
      // Re-armed explicitly, because the alternative doesn't work: leaning on
      // setArticle to change this effect's deps means a *failed* poll updates
      // no state, so the effect never re-runs, no next timer is scheduled, and
      // the POLL_LIMIT escape below never gets to fire either. One dropped
      // request and the spinner stays up forever.
      setPollTick((n) => n + 1);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [open, analyzing, article, articleId, navigate, pollTick]);

  const onAnalyze = async () => {
    // Reset here as well as in the load effect. It was only reset there, which
    // meant the POLL_LIMIT counter was per *dialog opening* rather than per
    // attempt: once a scan had exhausted its fifteen polls, the error it showed
    // — "try again in a moment" — offered something the component could no
    // longer do. Pressing the button re-armed the poll with the counter still
    // at the limit, so the very first tick failed it again, and the only way
    // out was to close the dialog and reopen it.
    polls.current = 0;
    setAnalyzing(true); setError("");
    try {
      const res = await analyzeFeedArticle(articleId);
      await refreshUsage();
      // Dismissed while the request was in flight. The scan still happened and
      // is cached for whoever asks next — but sending them to a report they
      // just closed is not what closing it meant.
      if (!openRef.current) return;
      if (res.status === "scored" && res.report_id) { navigate(`/report/${res.report_id}`); return; }
      setArticle((a) => ({ ...a, status: "scoring" }));   // 202: poll for the winner's result
    } catch (e) {
      const d = errDetail(e);
      await refreshUsage();
      if (!openRef.current) return;
      setAnalyzing(false);
      if (d.code === ERROR_CODES.SIGNUP_REQUIRED) setWall("signup");
      else if (d.code === ERROR_CODES.LIMIT_REACHED) setWall("limit");
      else {
        setError(d.message);
        toast.error(d.code === ERROR_CODES.CAPACITY ? "SlantScan is at capacity" : "Couldn't analyze that article");
      }
    }
  };

  const scored = article?.status === "scored" && article?.analysis;
  const s = scored ? leanStyle(article.analysis.lean_label) : null;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="article-peek">
          {/* The headline becomes the title once it loads, but until then the
              dialog would open with no accessible name at all — a screen reader
              announces "dialog" and nothing else. */}
          {!article && (
            <DialogTitle className="sr-only">
              {error ? "Article unavailable" : "Loading article"}
            </DialogTitle>
          )}

          {!article && !error && (
            <div className="space-y-3 py-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-6 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          )}

          {article && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 text-sm text-faint">
                  <OutletFavicon url={article.url} outlet={article.source} size={18} />
                  <span className="font-medium text-dim">{article.source}</span>
                  {when(article.published_at) && <span>· {when(article.published_at)}</span>}
                </div>
                <DialogTitle className="text-left font-serif text-xl leading-snug">
                  {article.title}
                </DialogTitle>
                {article.description && (
                  <DialogDescription className="text-left text-[15px] leading-relaxed text-dim">
                    {article.description}
                  </DialogDescription>
                )}
              </DialogHeader>

              {scored ? (
                <div data-testid="peek-scored">
                  <div className="rounded-xl border border-line/10 bg-subtle p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-widest text-faint">
                        Already scored
                      </span>
                      <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold ${s.text} ${s.bg} ${s.border}`}>
                        {article.analysis.lean_label}
                      </span>
                    </div>
                    <LeanGauge score={article.analysis.lean_score} label={article.analysis.lean_label}
                      confidence={article.analysis.confidence} />
                    <p className="mt-3 border-l-2 border-ink pl-3 text-sm italic leading-relaxed text-dim">
                      "{article.analysis.one_line_verdict}"
                    </p>
                  </div>
                  <Button data-testid="peek-view-report" onClick={() => navigate(`/report/${article.report_id}`)}
                    className="mt-3 h-12 w-full rounded-xl bg-ink text-paper hover:bg-ink/85">
                    Read the full report <ArrowRight size={17} className="ml-1.5" />
                  </Button>
                  <p className="mt-2 text-center text-xs text-faint">
                    Free to read — someone already ran this scan.
                  </p>
                </div>
              ) : article.status === "failed" ? (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>{article.error || "This article couldn't be read — it may be paywalled."}</span>
                </div>
              ) : (
                <div data-testid="peek-unscored">
                  {analyzing ? (
                    <LoadingStepper compact />
                  ) : (
                    <ClickSpark sparkColor={ink} sparkSize={9} sparkRadius={18} sparkCount={9}>
                      <Button data-testid="peek-analyze-btn" onClick={onAnalyze}
                        className="h-12 w-full rounded-xl bg-ink text-paper hover:bg-ink/85">
                        Analyze this article <Sparkles size={16} className="ml-1.5" />
                      </Button>
                    </ClickSpark>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-300/70 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200"
                  data-testid="peek-error">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              {article.url && (
                <a href={article.url} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-faint hover:text-ink">
                  <ExternalLink size={13} /> Open the original at {article.source}
                </a>
              )}
            </>
          )}

          {error && !article && (
            <p className="text-sm text-dim" data-testid="peek-load-error">{error}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Same two walls as the paste flow — a ticker scan spends the same allowance. */}
      <Dialog open={wall === "signup"} onOpenChange={(o) => !o && setWall(null)}>
        <DialogContent className="sm:max-w-md" data-testid="peek-signup-wall">
          <DialogHeader>
            <div className="mx-auto mb-1 grid h-12 w-12 place-items-center rounded-full bg-subtle text-ink"><Lock size={22} /></div>
            <DialogTitle className="text-center font-serif text-2xl">Create a free account to keep scanning</DialogTitle>
            <DialogDescription className="text-center">
              You've used your free scans. Sign up in seconds for 5 a day — no card needed. Reading
              articles other people have already scored stays free.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Button onClick={() => navigate("/signup")} className="h-12 rounded-xl bg-ink text-paper hover:bg-ink/85">Create free account</Button>
            <Button onClick={() => navigate("/signin")} variant="outline" className="h-12 rounded-xl border-line/25">I already have an account</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={wall === "limit"} onOpenChange={(o) => !o && setWall(null)}>
        <DialogContent className="sm:max-w-md" data-testid="peek-limit-wall">
          <DialogHeader>
            <div className="mx-auto mb-1 grid h-12 w-12 place-items-center rounded-full bg-subtle text-ink"><Zap size={22} /></div>
            <DialogTitle className="text-center font-serif text-2xl">You've hit today's free limit</DialogTitle>
            <DialogDescription className="text-center">
              Free accounts get 5 scans a day. Upgrade to Pro for unlimited scans, all highlights and compare mode.
            </DialogDescription>
          </DialogHeader>
          <Button onClick={() => navigate("/pricing")} className="h-12 rounded-xl bg-ink text-paper hover:bg-ink/85">See Pro</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
